import React, { useState, useRef, useEffect } from 'react';
import {
  Search,
  MessageSquare,
  Brain,
  FileText,
  Users,
  Building,
  Calendar,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Clock,
  Send,
  Copy,
  Download,
  Filter,
  RefreshCw,
  Star,
  Bookmark,
  Share,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  Plus,
  BarChart3,
  TrendingUp,
  Activity,
  Shield,
  Lock,
  Target,
  Flag,
  Heart,
  ThumbsUp,
  ThumbsDown,
  Info,
  ExternalLink,
  Archive,
  Settings,
  Globe,
  Mic,
  Languages,
  Users2,
  Calculator,
  Receipt,
  CreditCard,
  MapPin,
  Phone,
  Mail,
  Smartphone,
  Monitor,
  Tablet,
  Key,
  Fingerprint,
  Lightbulb,
  Cpu,
  Database,
  Network,
  Layers,
  Upload,
  FileUp,
  FileDown,
  CheckSquare,
  Square,
  XCircle,
  AlertTriangle,
  Home,
  Wrench,
  FileBarChart,
  Cog,
  Link,
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeft,
  Package,
  TrendingDown,
  User,
  Save,
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ActionMenu from '../common/ActionMenu';
import ConfirmationModal from '../common/ConfirmationModal';
import AIService from '../../services/aiService';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import ReactMarkdown from 'react-markdown';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const AISearchQA = () => {
  const { success, error: showError, info } = useToast();
  const [activeTab, setActiveTab] = useState('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [isAnswering, setIsAnswering] = useState(false);
  const [conversationContext, setConversationContext] = useState([]);
  const [testMode, setTestMode] = useState(
    process.env.NODE_ENV === 'development',
  );
  const [searchFilters, setSearchFilters] = useState({
    type: 'all',
    dateRange: 'all',
    status: 'all',
    priority: 'all',
  });
  const [nlQuery, setNlQuery] = useState('');
  const [isConverting, setIsConverting] = useState(false);
  const [queryResult, setQueryResult] = useState(null);
  const [queryHistory, setQueryHistory] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const chatEndRef = useRef(null);

  // Analytics and stats states
  const [stats, setStats] = useState({
    totalDocuments: 0,
    totalQuestions: 0,
    avgRelevance: 0,
    totalBookmarks: 0,
  });
  const [analyticsData, setAnalyticsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('30d');

  // Mock data for search results
  const [searchData, setSearchData] = useState([
    {
      id: 'SEARCH-001',
      title: 'Service Level Agreement - IT Services',
      type: 'Contract',
      category: 'Service Agreement',
      content:
        'This Service Level Agreement (SLA) defines the performance standards and service levels for IT services provided by the vendor...',
      relevanceScore: 95,
      lastModified: '2024-02-15T10:30:00Z',
      status: 'Active',
      priority: 'High',
      tags: ['sla', 'it-services', 'performance'],
      author: 'Legal Team',
      department: 'IT',
      wordCount: 2847,
      isBookmarked: false,
      isStarred: false,
      viewCount: 45,
      downloadCount: 12,
    },
    {
      id: 'SEARCH-002',
      title: 'Asset Management Policy - Hardware',
      type: 'Policy',
      category: 'Asset Management',
      content:
        'This policy outlines the procedures for managing hardware assets including procurement, deployment, maintenance, and disposal...',
      relevanceScore: 88,
      lastModified: '2024-02-14T09:15:00Z',
      status: 'Active',
      priority: 'Medium',
      tags: ['hardware', 'asset-management', 'policy'],
      author: 'IT Department',
      department: 'IT',
      wordCount: 1856,
      isBookmarked: true,
      isStarred: false,
      viewCount: 32,
      downloadCount: 8,
    },
    {
      id: 'SEARCH-003',
      title: 'Contract Renewal Process',
      type: 'Process',
      category: 'Contract Management',
      content:
        'Step-by-step process for contract renewal including review procedures, approval workflows, and notification systems...',
      relevanceScore: 92,
      lastModified: '2024-02-13T11:20:00Z',
      status: 'Active',
      priority: 'High',
      tags: ['renewal', 'process', 'workflow'],
      author: 'Contract Team',
      department: 'Legal',
      wordCount: 1234,
      isBookmarked: false,
      isStarred: true,
      viewCount: 67,
      downloadCount: 15,
    },
  ]);

  // Mock chat history
  const [chatData, setChatData] = useState([
    {
      id: 'CHAT-001',
      question: 'What are the key terms in our IT service contracts?',
      answer:
        'Based on our contract database, the key terms in IT service contracts typically include: 1) Service Level Agreements (SLAs) with 99.9% uptime requirements, 2) Payment terms of Net 30 days, 3) Data security and confidentiality clauses, 4) Termination notice periods of 60-90 days, and 5) Intellectual property rights protection.',
      timestamp: '2024-02-16T14:30:00Z',
      category: 'Contract Analysis',
      confidence: 94,
      sources: ['SEARCH-001', 'SEARCH-003'],
      isHelpful: true,
      isBookmarked: false,
    },
    {
      id: 'CHAT-002',
      question: 'How do we handle asset depreciation calculations?',
      answer:
        'Our asset depreciation follows the straight-line method over the useful life of assets. For IT hardware, we typically use 3-5 years depreciation period. Software licenses are depreciated over their license term. The calculations are automated in our financial system and reviewed quarterly.',
      timestamp: '2024-02-16T13:45:00Z',
      category: 'Asset Management',
      confidence: 89,
      sources: ['SEARCH-002'],
      isHelpful: true,
      isBookmarked: true,
    },
  ]);

  // Fetch statistics from backend
  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const response = await AIService.getSearchStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching search stats:', error);
      // Fallback to mock data
      setStats({
        totalDocuments: searchData.length,
        totalQuestions: chatData.length,
        avgRelevance: Math.round(
          searchData.reduce((sum, item) => sum + item.relevanceScore, 0) /
            searchData.length,
        ),
        totalBookmarks:
          searchData.filter((item) => item.isBookmarked).length +
          chatData.filter((item) => item.isBookmarked).length,
      });
    } finally {
      setStatsLoading(false);
    }
  };

  // Fetch analytics data from backend
  const fetchAnalytics = async (timeframe = '30d') => {
    setAnalyticsLoading(true);
    try {
      const response = await AIService.getSearchAnalytics(timeframe);
      if (response.success) {
        setAnalyticsData(response.data);
      }
    } catch (error) {
      console.error('Error fetching search analytics:', error);
      // Fallback to mock data
      setAnalyticsData({
        timeframe,
        metrics: {
          totalSearches: 2847,
          totalQuestions: 342,
          avgRelevanceScore: 92,
          avgResponseTime: 1.8,
          totalBookmarks: 156,
          userSatisfaction: 94,
        },
        searchTrends: {
          popularQueries: [
            { query: 'contract renewal process', count: 45 },
            { query: 'asset depreciation calculation', count: 38 },
            { query: 'service level agreement terms', count: 32 },
          ],
        },
      });
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchStats();
    fetchAnalytics(selectedTimeframe);
  }, []);

  // Calculate statistics (fallback)
  const calculatedStats = {
    totalDocuments: stats.totalDocuments || searchData.length,
    totalQuestions: stats.totalQuestions || chatData.length,
    averageRelevance:
      stats.avgRelevance ||
      Math.round(
        searchData.reduce((sum, item) => sum + item.relevanceScore, 0) /
          searchData.length,
      ),
    totalViews: searchData.reduce((sum, item) => sum + item.viewCount, 0),
    bookmarkedItems:
      stats.totalBookmarks ||
      searchData.filter((item) => item.isBookmarked).length +
        chatData.filter((item) => item.isBookmarked).length,
  };

  // Filtered data
  const filteredSearchData = searchData.filter((item) => {
    const matchesSearch =
      searchQuery === '' ||
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.tags.some((tag) =>
        tag.toLowerCase().includes(searchQuery.toLowerCase()),
      );

    const matchesType =
      searchFilters.type === 'all' || item.type === searchFilters.type;
    const matchesStatus =
      searchFilters.status === 'all' || item.status === searchFilters.status;
    const matchesPriority =
      searchFilters.priority === 'all' ||
      item.priority === searchFilters.priority;

    return matchesSearch && matchesType && matchesStatus && matchesPriority;
  });

  const tabs = [
    { id: 'search', label: 'AI Search', icon: <Search size={18} /> },
    { id: 'qa', label: 'Q&A Chat', icon: <MessageSquare size={18} /> },
    {
      id: 'nl-query',
      label: 'Natural Language Query',
      icon: <Database size={18} />,
    },
    { id: 'history', label: 'Search History', icon: <Clock size={18} /> },
    { id: 'analytics', label: 'Analytics', icon: <BarChart3 size={18} /> },
  ];

  // Define columns for DataTable
  const columns = [
    {
      key: 'title',
      label: 'Document',
      width: '300px',
      render: (value, row) => (
        <div>
          <div
            style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}
          >
            {value}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {row.type} • {row.category} • {row.wordCount} words
          </div>
        </div>
      ),
    },
    {
      key: 'relevanceScore',
      label: 'Relevance',
      width: '120px',
      render: (score) => {
        const getScoreColor = (score) => {
          if (score >= 90) return '#10b981';
          if (score >= 70) return '#f59e0b';
          return '#ef4444';
        };

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '60px',
                height: '6px',
                backgroundColor: '#e5e7eb',
                borderRadius: '0',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${score}%`,
                  height: '100%',
                  backgroundColor: getScoreColor(score),
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
            <span
              style={{
                fontSize: '12px',
                fontWeight: '500',
                color: getScoreColor(score),
              }}
            >
              {score}%
            </span>
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      width: '100px',
      render: (status) => {
        const statusConfig = {
          Active: {
            color: '#10b981',
            bg: '#d1fae5',
            icon: <CheckCircle size={14} />,
          },
          Draft: { color: '#f59e0b', bg: '#fef3c7', icon: <Edit size={14} /> },
          Archived: {
            color: '#6b7280',
            bg: '#f3f4f6',
            icon: <Archive size={14} />,
          },
        };
        const config = statusConfig[status] || statusConfig['Active'];

        return (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 10px',
              borderRadius: '0',
              fontSize: '12px',
              fontWeight: '500',
              backgroundColor: config.bg,
              color: config.color,
              width: 'fit-content',
            }}
          >
            {config.icon}
            {status}
          </span>
        );
      },
    },
    {
      key: 'viewCount',
      label: 'Views',
      width: '80px',
      render: (count) => (
        <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
          {count}
        </div>
      ),
    },
    {
      key: 'lastModified',
      label: 'Modified',
      width: '120px',
      render: (date) => new Date(date).toLocaleDateString(),
    },
  ];

  // Handler functions
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      // Call AI intelligent search API - queries backend database
      const response = await AIService.intelligentSearch(
        searchQuery,
        searchFilters,
      );

      if (response.success && response.results) {
        // Format results to match the expected structure
        const formattedResults = response.results.map((result) => ({
          id: result.id,
          title: result.title,
          type: result.type,
          category: result.category,
          content: result.content,
          relevanceScore: result.relevanceScore || 85,
          lastModified: result.lastModified || new Date().toISOString(),
          status: result.status || 'Active',
          priority: result.priority || 'Medium',
          tags: result.tags || [result.type, result.status].filter(Boolean),
          author: result.author || 'System',
          department:
            result.department ||
            (result.type === 'Contract' ? 'Legal' : 'Operations'),
          wordCount:
            result.wordCount || (result.content || '').split(' ').length,
          isBookmarked: result.isBookmarked || false,
          isStarred: result.isStarred || false,
          viewCount: result.viewCount || 0,
          downloadCount: result.downloadCount || 0,
        }));

        setSearchResults(formattedResults);
        // Update search data with new results (merge with existing, avoid duplicates)
        setSearchData((prev) => {
          const existingIds = new Set(prev.map((item) => item.id));
          const newResults = formattedResults.filter(
            (result) => !existingIds.has(result.id),
          );
          return [...formattedResults, ...prev];
        });

        success(
          `Found ${formattedResults.length} result${formattedResults.length !== 1 ? 's' : ''} for "${searchQuery}"`,
        );
      } else {
        throw new Error('No results returned from search');
      }
    } catch (error) {
      console.error('Error performing search:', error);
      showError(`Search failed: ${error.message}. Please try again.`);

      // Fallback to local search if available
      if (searchData.length > 0) {
        const mockResults = searchData.filter(
          (item) =>
            item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (item.tags &&
              item.tags.some((tag) =>
                tag.toLowerCase().includes(searchQuery.toLowerCase()),
              )),
        );
        setSearchResults(mockResults);
        if (mockResults.length > 0) {
          success(
            `Found ${mockResults.length} result${mockResults.length !== 1 ? 's' : ''} from cached data`,
          );
        }
      } else {
        setSearchResults([]);
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleAskQuestion = async (questionText = null) => {
    // Ensure question is always a string
    const question = String(questionText || currentQuestion || '').trim();
    if (!question) return;

    setIsAnswering(true);
    try {
      // Build context from conversation history for AI understanding
      const contextMessages = conversationContext
        .slice(-3)
        .map(
          (ctx) =>
            `Previous Q: ${ctx.question}\nPrevious A: ${ctx.answer.substring(0, 300)}...`,
        )
        .join('\n\n');

      // Build context string - include conversation history if available
      const context = contextMessages
        ? `Previous conversation context:\n${contextMessages}\n\nCurrent question: ${question}`
        : `You are CAM360 AI assistant, a specialized AI for Contract LifeCycle Management. Current question: ${question}`;

      // Call AI service for Q&A - pass question and context separately
      const response = await AIService.askQuestion(question, context);

      // Ensure answer is a string
      const answerText =
        typeof response.answer === 'string'
          ? response.answer
          : String(response.answer || 'No answer provided');

      const newChat = {
        id: `CHAT-${Date.now()}`,
        question: String(question), // Ensure question is always a string
        answer: answerText,
        timestamp: new Date().toISOString(),
        category: response.category || 'General',
        confidence: response.confidence || 85,
        sources: response.sources || [],
        followUpQuestions: response.followUpQuestions || [],
        dataFound: response.dataFound || false,
        isHelpful: false,
        isBookmarked: false,
      };

      // Add to conversation context
      setConversationContext((prev) =>
        [
          ...prev,
          {
            question: String(question),
            answer: answerText,
          },
        ].slice(-5),
      ); // Keep last 5 exchanges

      setChatHistory((prev) => [newChat, ...prev]);
      setChatData((prev) => [newChat, ...prev]);
      setCurrentQuestion('');
    } catch (error) {
      console.error('Error asking question:', error);
      // Fallback to mock response
      const mockResponse = {
        id: `CHAT-${Date.now()}`,
        question: String(question), // Ensure question is always a string
        answer:
          'I understand your question about Contract LifeCycle Management. Based on our knowledge base, I can help you with information about contracts, assets, compliance, and related processes. Could you please provide more specific details?',
        timestamp: new Date().toISOString(),
        category: 'General',
        confidence: 75,
        sources: [],
        followUpQuestions: [],
        dataFound: false,
        isHelpful: false,
        isBookmarked: false,
      };

      setChatHistory((prev) => [mockResponse, ...prev]);
      setChatData((prev) => [mockResponse, ...prev]);
      setCurrentQuestion('');
    } finally {
      setIsAnswering(false);
    }
  };

  const handleFollowUpClick = (followUpQuestion) => {
    // Ensure it's a string
    const question = String(followUpQuestion || '').trim();
    if (!question) return;

    setCurrentQuestion(question);
    // Auto-submit after a brief delay to show the question in the input
    setTimeout(() => {
      handleAskQuestion(question);
    }, 100);
  };

  // Clear function - tab specific
  const handleClear = () => {
    if (activeTab === 'search') {
      setSearchQuery('');
      setSearchResults([]);
      setSearchFilters({
        type: 'all',
        dateRange: 'all',
        status: 'all',
        priority: 'all',
      });
      success('Search cleared');
    } else if (activeTab === 'qa') {
      setCurrentQuestion('');
      setChatHistory([]);
      setConversationContext([]);
      success('Chat cleared');
    } else if (activeTab === 'nl-query') {
      setNlQuery('');
      setQueryResult(null);
      setQueryHistory([]);
      success('Natural language query cleared');
    }
  };

  const handleView = (item) => {
    //console.log('Viewing item:', item.title);
  };

  const handleBookmark = (item) => {
    setSearchData((prev) =>
      prev.map((searchItem) =>
        searchItem.id === item.id
          ? { ...searchItem, isBookmarked: !searchItem.isBookmarked }
          : searchItem,
      ),
    );
  };

  const handleDelete = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      setSearchData((prev) =>
        prev.filter((item) => item.id !== selectedItem.id),
      );
      setShowDeleteModal(false);
      setSelectedItem(null);
    } catch (error) {
      console.error('Error deleting item:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Helper function to generate insights and charts from query results
  const generateInsights = (queryResult, originalQuery) => {
    if (
      !queryResult ||
      !queryResult.results ||
      queryResult.results.length === 0
    ) {
      return {
        answer: `I couldn't find any ${queryResult?.entityType?.toLowerCase() || 'records'} matching your query "${originalQuery}". Please try adjusting your search criteria.`,
        charts: [],
      };
    }

    const results = queryResult.results;
    const entityType = queryResult.entityType;
    const charts = [];
    let answer = '';

    // Generate natural language answer
    if (entityType === 'Contract') {
      const totalValue = results.reduce((sum, r) => sum + (r.value || 0), 0);
      const avgValue = totalValue / results.length;
      const statusCounts = {};
      const typeCounts = {};

      results.forEach((r) => {
        statusCounts[r.status] = (statusCounts[r.status] || 0) + 1;
        typeCounts[r.type] = (typeCounts[r.type] || 0) + 1;
      });

      answer = `I found ${results.length} contract${results.length !== 1 ? 's' : ''} matching your query. `;
      answer += `The total contract value is ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalValue)}, `;
      answer += `with an average value of ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(avgValue)}. `;

      if (Object.keys(statusCounts).length > 1) {
        const statusList = Object.entries(statusCounts)
          .map(([status, count]) => `${count} ${status}`)
          .join(', ');
        answer += `Status breakdown: ${statusList}.`;
      }

      // Status distribution pie chart
      if (Object.keys(statusCounts).length > 0) {
        charts.push({
          type: 'pie',
          title: 'Contracts by Status',
          data: Object.entries(statusCounts).map(([name, value]) => ({
            name,
            value,
          })),
        });
      }

      // Type distribution bar chart
      if (Object.keys(typeCounts).length > 1) {
        charts.push({
          type: 'bar',
          title: 'Contracts by Type',
          data: Object.entries(typeCounts).map(([name, value]) => ({
            name,
            value,
          })),
        });
      }

      // Value distribution (top 10)
      if (results.length > 1) {
        const topContracts = [...results]
          .sort((a, b) => (b.value || 0) - (a.value || 0))
          .slice(0, 10)
          .map((r) => ({
            name: r.title?.substring(0, 20) || r.contractId || 'Unknown',
            value: r.value || 0,
          }));

        if (topContracts.length > 0) {
          charts.push({
            type: 'bar',
            title: 'Top Contracts by Value',
            data: topContracts,
          });
        }
      }
    } else if (entityType === 'Asset') {
      const totalValue = results.reduce((sum, r) => sum + (r.value || 0), 0);
      const avgValue = totalValue / results.length;
      const statusCounts = {};
      const typeCounts = {};
      const categoryCounts = {};

      results.forEach((r) => {
        statusCounts[r.status] = (statusCounts[r.status] || 0) + 1;
        if (r.type) typeCounts[r.type] = (typeCounts[r.type] || 0) + 1;
        if (r.category)
          categoryCounts[r.category] = (categoryCounts[r.category] || 0) + 1;
      });

      answer = `I found ${results.length} asset${results.length !== 1 ? 's' : ''} matching your query. `;
      answer += `The total asset value is ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalValue)}, `;
      answer += `with an average value of ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(avgValue)}.`;

      // Status distribution pie chart
      if (Object.keys(statusCounts).length > 0) {
        charts.push({
          type: 'pie',
          title: 'Assets by Status',
          data: Object.entries(statusCounts).map(([name, value]) => ({
            name,
            value,
          })),
        });
      }

      // Type distribution bar chart
      if (Object.keys(typeCounts).length > 1) {
        charts.push({
          type: 'bar',
          title: 'Assets by Type',
          data: Object.entries(typeCounts).map(([name, value]) => ({
            name,
            value,
          })),
        });
      }

      // Category distribution
      if (Object.keys(categoryCounts).length > 1) {
        charts.push({
          type: 'bar',
          title: 'Assets by Category',
          data: Object.entries(categoryCounts).map(([name, value]) => ({
            name,
            value,
          })),
        });
      }
    } else if (entityType === 'Vendor' || entityType === 'Customer') {
      const statusCounts = {};
      const typeCounts = {};

      results.forEach((r) => {
        statusCounts[r.status] = (statusCounts[r.status] || 0) + 1;
        if (r.type) typeCounts[r.type] = (typeCounts[r.type] || 0) + 1;
      });

      answer = `I found ${results.length} ${entityType.toLowerCase()}${results.length !== 1 ? 's' : ''} matching your query.`;

      // Status distribution pie chart
      if (Object.keys(statusCounts).length > 0) {
        charts.push({
          type: 'pie',
          title: `${entityType}s by Status`,
          data: Object.entries(statusCounts).map(([name, value]) => ({
            name,
            value,
          })),
        });
      }

      // Type distribution bar chart
      if (Object.keys(typeCounts).length > 1) {
        charts.push({
          type: 'bar',
          title: `${entityType}s by Type`,
          data: Object.entries(typeCounts).map(([name, value]) => ({
            name,
            value,
          })),
        });
      }
    } else if (entityType === 'Project') {
      const statusCounts = {};
      const totalBudget = results.reduce((sum, r) => sum + (r.budget || 0), 0);

      results.forEach((r) => {
        statusCounts[r.status] = (statusCounts[r.status] || 0) + 1;
      });

      answer = `I found ${results.length} project${results.length !== 1 ? 's' : ''} matching your query. `;
      answer += `Total budget across all projects: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalBudget)}.`;

      // Status distribution pie chart
      if (Object.keys(statusCounts).length > 0) {
        charts.push({
          type: 'pie',
          title: 'Projects by Status',
          data: Object.entries(statusCounts).map(([name, value]) => ({
            name,
            value,
          })),
        });
      }
    } else {
      // Mixed or unknown entity type
      answer = `I found ${results.length} record${results.length !== 1 ? 's' : ''} matching your query.`;
    }

    return { answer, charts };
  };

  const handleNaturalLanguageQuery = async () => {
    if (!nlQuery.trim()) return;

    setIsConverting(true);
    try {
      // Call AI natural language to query conversion API
      const response = await AIService.naturalLanguageToQuery(
        nlQuery,
        'contract-asset-management',
      );

      if (response.success) {
        // Generate insights and charts
        const insights = generateInsights(response.queryResult, nlQuery);
        const enhancedResult = {
          ...response.queryResult,
          answer: insights.answer,
          charts: insights.charts,
        };

        setQueryResult(enhancedResult);

        // Add to query history
        const newQuery = {
          id: `QUERY-${Date.now()}`,
          naturalLanguage: nlQuery,
          queryResult: enhancedResult,
          timestamp: new Date().toISOString(),
          confidence: response.queryResult.confidence,
        };

        setQueryHistory((prev) => [newQuery, ...prev]);

        // Show success message
        if (
          response.queryResult.results &&
          response.queryResult.results.length > 0
        ) {
          success(`Found ${response.queryResult.results.length} result(s)`);
        } else {
          info('Query executed successfully, but no results found');
        }

        setNlQuery('');
      } else {
        showError('Failed to convert query');
      }
    } catch (error) {
      console.error('Error converting natural language to query:', error);
      showError(error.message || 'Failed to convert natural language query');

      // Fallback response
      setQueryResult({
        queryType: 'SEARCH',
        explanation: 'Error occurred while converting query. Please try again.',
        confidence: 0,
        results: [],
        totalCount: 0,
        resultCount: 0,
        answer:
          'I encountered an error while processing your query. Please try again.',
        charts: [],
      });
    } finally {
      setIsConverting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (activeTab === 'search') {
        handleSearch();
      } else if (activeTab === 'qa') {
        handleAskQuestion();
      } else if (activeTab === 'nl-query') {
        handleNaturalLanguageQuery();
      }
    }
  };

  // Auto-scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  // Function to fill with test data - tab specific
  const fillTestData = () => {
    if (activeTab === 'search') {
      // Fill search query for AI Search tab
      const sampleSearchQueries = [
        'IT service contracts',
        'laptops',
        'vendor ABC',
        'active contracts',
        'maintenance equipment',
        'software licenses',
        'office furniture',
        'service agreements',
        'hardware assets',
        'contract renewals',
      ];

      const randomIndex = Math.floor(
        Math.random() * sampleSearchQueries.length,
      );
      const selectedQuery = sampleSearchQueries[randomIndex];

      setSearchQuery(selectedQuery);
      success(
        `Search query filled: "${selectedQuery}" - Click Search to find results!`,
      );

      // Optionally auto-trigger search
      setTimeout(() => {
        handleSearch();
      }, 300);
    } else if (activeTab === 'qa') {
      // Fill question for Q&A Chat tab
      const sampleQuestions = [
        'What are the key terms in our IT service contracts?',
        'How do we handle asset depreciation calculations?',
        'What is our vendor management process?',
        'What are our GDPR compliance requirements?',
        'Show me all active contracts expiring in the next 30 days',
        'What assets need maintenance this month?',
        'How many contracts do we have with each vendor?',
        'What is the total value of all our active contracts?',
        'Which assets are under warranty and when do they expire?',
        'What are the payment terms in our service agreements?',
        'Show me all contracts that are up for renewal',
        'What is the status of our critical assets?',
        'How do we calculate ROI for asset investments?',
        'What compliance requirements apply to our contracts?',
        'Which vendors have the highest contract values?',
      ];

      const randomIndex = Math.floor(Math.random() * sampleQuestions.length);
      const selectedQuestion = sampleQuestions[randomIndex];

      setCurrentQuestion(selectedQuestion);
      success(
        `Sample question filled: "${selectedQuestion}" - Click Ask to get AI response!`,
      );
    } else if (activeTab === 'nl-query') {
      // Fill natural language query for NL Query tab
      const sampleNLQueries = [
        'Show me all active contracts',
        'Find assets expiring this month',
        'List contracts with value over $100,000',
        'Show users in IT department',
        'Find contracts signed by John Smith',
        'Display all assets in Building A',
        'Show contracts expiring in next 30 days',
      ];

      const randomIndex = Math.floor(Math.random() * sampleNLQueries.length);
      const selectedQuery = sampleNLQueries[randomIndex];

      setNlQuery(selectedQuery);
      success(
        `Natural language query filled: "${selectedQuery}" - Click Convert to see the query!`,
      );
    } else {
      success(
        'Fill Test Data is available for Search, Q&A, and NL Query tabs.',
      );
    }
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="AI Search & Q&A"
        description="Intelligent search and AI-powered question answering for contracts and assets"
        actions={
          testMode
            ? [
                {
                  label: `Fill Test Data (${activeTab === 'search' ? 'Search' : activeTab === 'qa' ? 'Q&A' : activeTab === 'nl-query' ? 'NL Query' : 'Current Tab'})`,
                  icon: <Database size={18} />,
                  onClick: fillTestData,
                  variant: 'outline',
                },
              ]
            : []
        }
      />
      <div className="page-container">
        {/* Tabbed Navigation */}
        <div className="project-tabs">
          <div className="project-tabs-nav">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`project-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <div className="project-tab-icon">{tab.icon}</div>
                <div className="project-tab-content">
                  <div className="project-tab-label">{tab.label}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* AI Search Tab */}
        {activeTab === 'search' && (
          <div>
            {/* Search Input Section */}
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '0',
                padding: '20px',
                marginBottom: '20px',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px',
                }}
              >
                <h3
                  style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1f2937',
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Search size={20} style={{ color: 'var(--primary-color)' }} />
                  Intelligent Search
                </h3>
                <button
                  onClick={handleClear}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    backgroundColor: 'transparent',
                    color: '#6b7280',
                    border: '1px solid #d1d5db',
                    borderRadius: '0',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontFamily:
                      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f3f4f6';
                    e.target.style.color = '#1f2937';
                    e.target.style.borderColor = '#9ca3af';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#6b7280';
                    e.target.style.borderColor = '#d1d5db';
                  }}
                >
                  <XCircle size={14} />
                  Clear
                </button>
              </div>
              <p
                style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  margin: '0 0 16px 0',
                }}
              >
                Search across your contracts, assets, vendors, and projects
                using natural language or keywords
              </p>

              {/* Search Input */}
              <div
                style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}
              >
                <div style={{ flex: 1, position: 'relative' }}>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                    placeholder="Search for contracts, assets, vendors... (e.g., 'IT service contracts', 'laptops', 'vendor ABC')"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily:
                        'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    }}
                  />
                </div>
                <button
                  onClick={handleSearch}
                  disabled={isSearching || !searchQuery.trim()}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 24px',
                    backgroundColor:
                      isSearching || !searchQuery.trim()
                        ? '#9ca3af'
                        : 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor:
                      isSearching || !searchQuery.trim()
                        ? 'not-allowed'
                        : 'pointer',
                    fontFamily:
                      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    height: 'fit-content',
                  }}
                >
                  {isSearching ? (
                    <>
                      <RefreshCw
                        size={16}
                        style={{ animation: 'spin 1s linear infinite' }}
                      />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search size={16} />
                      Search
                    </>
                  )}
                </button>
              </div>

              {/* Search Filters */}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <select
                  value={searchFilters.type}
                  onChange={(e) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      type: e.target.value,
                    }))
                  }
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily:
                      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  }}
                >
                  <option value="all">All Types</option>
                  <option value="Contract">Contracts</option>
                  <option value="Asset">Assets</option>
                  <option value="Vendor">Vendors</option>
                  <option value="Project">Projects</option>
                </select>

                <select
                  value={searchFilters.status}
                  onChange={(e) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily:
                      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  }}
                >
                  <option value="all">All Statuses</option>
                  <option value="Active">Active</option>
                  <option value="Draft">Draft</option>
                  <option value="Archived">Archived</option>
                </select>
              </div>
            </div>

            {/* Search Results */}
            {searchQuery && (
              <div
                style={{
                  marginBottom: '16px',
                  fontSize: '14px',
                  color: '#6b7280',
                }}
              >
                {isSearching ? (
                  <span>Searching...</span>
                ) : searchResults.length > 0 ? (
                  <span>
                    Found {searchResults.length} result
                    {searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
                  </span>
                ) : (
                  <span>No results found for "{searchQuery}"</span>
                )}
              </div>
            )}

            <div className="page-table-container">
              <DataTable
                data={searchQuery ? searchResults : filteredSearchData}
                columns={columns}
                title={
                  searchQuery
                    ? `Search Results for "${searchQuery}"`
                    : 'AI Search Results'
                }
                searchable={true}
                sortable={true}
                exportable={true}
                pagination={true}
                pageSize={10}
                showActions={true}
                showCheckboxes={false}
                onView={handleView}
                onEdit={handleBookmark}
                onDelete={handleDelete}
                searchPlaceholder="Search within results..."
                emptyMessage={
                  searchQuery
                    ? `No results found for "${searchQuery}". Try a different search term.`
                    : 'Enter a search query above to find contracts, assets, and other records.'
                }
                initialHiddenColumns={['id']}
              />
            </div>
          </div>
        )}

        {/* Q&A Chat Tab */}
        {activeTab === 'qa' && (
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '0',
              padding: '20px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
              height: 'calc(100vh - 320px)',
              minHeight: '500px',
              maxHeight: '700px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <h3
                style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1f2937',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Brain size={20} style={{ color: 'var(--primary-color)' }} />
                AI Assistant
              </h3>
              <button
                onClick={handleClear}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  backgroundColor: 'transparent',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: '0',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily:
                    'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f3f4f6';
                  e.target.style.color = '#1f2937';
                  e.target.style.borderColor = '#9ca3af';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#6b7280';
                  e.target.style.borderColor = '#d1d5db';
                }}
              >
                <XCircle size={14} />
                Clear
              </button>
            </div>

            {/* Chat History */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#f8fafc',
                borderRadius: '0',
                border: '1px solid #e5e7eb',
                minHeight: 0,
              }}
            >
              {chatHistory.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: '16px',
                    padding: '40px 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                  }}
                >
                  <Brain
                    size={48}
                    style={{ color: '#d1d5db', marginBottom: '16px' }}
                  />
                  <p style={{ margin: 0 }}>
                    Ask me anything about your contracts, assets, or policies!
                  </p>
                </div>
              ) : (
                chatHistory.map((chat) => (
                  <div key={chat.id} style={{ marginBottom: '20px' }}>
                    {/* User Question */}
                    <div
                      style={{
                        backgroundColor: '#e5e7eb',
                        padding: '10px 14px',
                        borderRadius: '0',
                        marginBottom: '6px',
                        fontSize: '14px',
                        color: '#374151',
                        fontWeight: '500',
                      }}
                    >
                      <strong>Q:</strong> {String(chat.question || '')}
                    </div>

                    {/* AI Answer */}
                    <div
                      style={{
                        backgroundColor: 'rgba(70, 120, 190, 0.08)',
                        padding: '14px 16px',
                        borderRadius: '0',
                        fontSize: '14px',
                        color: '#1f2937',
                        border: '1px solid rgba(70, 120, 190, 0.2)',
                        lineHeight: '1.6',
                        marginBottom:
                          chat.followUpQuestions &&
                          chat.followUpQuestions.length > 0
                            ? '12px'
                            : '0',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '10px',
                        }}
                      >
                        <strong style={{ color: '#1f2937' }}>A:</strong>
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                          }}
                        >
                          {chat.dataFound && (
                            <span
                              style={{
                                fontSize: '11px',
                                color: '#10b981',
                                fontWeight: '600',
                                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                padding: '2px 6px',
                                borderRadius: '3px',
                              }}
                            >
                              ✓ Data Found
                            </span>
                          )}
                          <span
                            style={{
                              fontSize: '12px',
                              color: '#6b7280',
                              fontWeight: '500',
                            }}
                          >
                            Confidence: {chat.confidence}%
                          </span>
                        </div>
                      </div>
                      <div
                        style={{
                          fontFamily:
                            'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                          lineHeight: '1.7',
                        }}
                      >
                        <ReactMarkdown
                          components={{
                            h1: ({ children }) => (
                              <h1
                                style={{
                                  fontSize: '18px',
                                  fontWeight: 'bold',
                                  margin: '12px 0 8px 0',
                                  color: '#1f2937',
                                  lineHeight: '1.4',
                                }}
                              >
                                {children}
                              </h1>
                            ),
                            h2: ({ children }) => (
                              <h2
                                style={{
                                  fontSize: '16px',
                                  fontWeight: 'bold',
                                  margin: '10px 0 6px 0',
                                  color: '#1f2937',
                                  lineHeight: '1.4',
                                }}
                              >
                                {children}
                              </h2>
                            ),
                            h3: ({ children }) => (
                              <h3
                                style={{
                                  fontSize: '15px',
                                  fontWeight: 'bold',
                                  margin: '8px 0 4px 0',
                                  color: '#1f2937',
                                  lineHeight: '1.4',
                                }}
                              >
                                {children}
                              </h3>
                            ),
                            p: ({ children }) => (
                              <p
                                style={{
                                  margin: '6px 0',
                                  lineHeight: '1.7',
                                  color: '#374151',
                                }}
                              >
                                {children}
                              </p>
                            ),
                            ul: ({ children }) => (
                              <ul
                                style={{
                                  margin: '8px 0',
                                  paddingLeft: '20px',
                                  lineHeight: '1.7',
                                }}
                              >
                                {children}
                              </ul>
                            ),
                            ol: ({ children }) => (
                              <ol
                                style={{
                                  margin: '8px 0',
                                  paddingLeft: '20px',
                                  lineHeight: '1.7',
                                }}
                              >
                                {children}
                              </ol>
                            ),
                            li: ({ children }) => (
                              <li
                                style={{
                                  margin: '4px 0',
                                  lineHeight: '1.6',
                                  color: '#374151',
                                }}
                              >
                                {children}
                              </li>
                            ),
                            strong: ({ children }) => (
                              <strong
                                style={{ fontWeight: '600', color: '#1f2937' }}
                              >
                                {children}
                              </strong>
                            ),
                            em: ({ children }) => (
                              <em
                                style={{
                                  fontStyle: 'italic',
                                  color: '#374151',
                                }}
                              >
                                {children}
                              </em>
                            ),
                            code: ({ children }) => (
                              <code
                                style={{
                                  backgroundColor: '#f3f4f6',
                                  padding: '2px 6px',
                                  borderRadius: '3px',
                                  fontSize: '13px',
                                  fontFamily:
                                    'Monaco, Consolas, "Courier New", monospace',
                                  color: '#1f2937',
                                }}
                              >
                                {children}
                              </code>
                            ),
                            blockquote: ({ children }) => (
                              <blockquote
                                style={{
                                  borderLeft: '3px solid var(--primary-color)',
                                  paddingLeft: '12px',
                                  margin: '10px 0',
                                  fontStyle: 'italic',
                                  color: '#6b7280',
                                  backgroundColor: 'rgba(70, 120, 190, 0.05)',
                                }}
                              >
                                {children}
                              </blockquote>
                            ),
                            hr: () => (
                              <hr
                                style={{
                                  border: 'none',
                                  borderTop: '1px solid #e5e7eb',
                                  margin: '12px 0',
                                }}
                              />
                            ),
                          }}
                        >
                          {chat.answer}
                        </ReactMarkdown>
                      </div>
                    </div>

                    {/* Follow-up Questions */}
                    {chat.followUpQuestions &&
                      chat.followUpQuestions.length > 0 && (
                        <div
                          style={{
                            marginTop: '12px',
                            padding: '12px',
                            backgroundColor: '#f0f9ff',
                            border: '1px solid rgba(70, 120, 190, 0.2)',
                            borderRadius: '0',
                          }}
                        >
                          <div
                            style={{
                              fontSize: '12px',
                              fontWeight: '600',
                              color: '#1f2937',
                              marginBottom: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}
                          >
                            <Lightbulb
                              size={14}
                              style={{ color: 'var(--primary-color)' }}
                            />
                            Suggested follow-up questions:
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '6px',
                            }}
                          >
                            {chat.followUpQuestions.map((followUp, idx) => (
                              <button
                                key={idx}
                                onClick={() => handleFollowUpClick(followUp)}
                                disabled={isAnswering}
                                style={{
                                  textAlign: 'left',
                                  padding: '8px 12px',
                                  backgroundColor: 'white',
                                  border: '1px solid rgba(70, 120, 190, 0.3)',
                                  borderRadius: '0',
                                  fontSize: '13px',
                                  color: '#1f2937',
                                  cursor: isAnswering
                                    ? 'not-allowed'
                                    : 'pointer',
                                  fontFamily:
                                    'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                                  transition: 'all 0.2s',
                                  opacity: isAnswering ? 0.6 : 1,
                                }}
                                onMouseEnter={(e) => {
                                  if (!isAnswering) {
                                    e.target.style.backgroundColor =
                                      'rgba(70, 120, 190, 0.1)';
                                    e.target.style.borderColor =
                                      'var(--primary-color)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isAnswering) {
                                    e.target.style.backgroundColor = 'white';
                                    e.target.style.borderColor =
                                      'rgba(70, 120, 190, 0.3)';
                                  }
                                }}
                              >
                                {followUp}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                ))
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Question Input */}
            <div style={{ display: 'flex', gap: '12px', flexShrink: 0 }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <textarea
                  value={currentQuestion}
                  onChange={(e) => setCurrentQuestion(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask a question about contracts, assets, or policies..."
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily:
                      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    resize: 'none',
                    lineHeight: '1.5',
                  }}
                />
              </div>
              <button
                onClick={handleAskQuestion}
                disabled={isAnswering || !currentQuestion.trim()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  backgroundColor:
                    isAnswering || !currentQuestion.trim()
                      ? '#9ca3af'
                      : 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor:
                    isAnswering || !currentQuestion.trim()
                      ? 'not-allowed'
                      : 'pointer',
                  fontFamily:
                    'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  height: 'fit-content',
                  alignSelf: 'flex-end',
                }}
              >
                {isAnswering ? (
                  <>
                    <RefreshCw
                      size={16}
                      style={{ animation: 'spin 1s linear infinite' }}
                    />
                    Thinking...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Ask
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Natural Language Query Tab */}
        {activeTab === 'nl-query' && (
          <div>
            {/* Natural Language Query Interface */}
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '0',
                padding: '24px',
                marginBottom: '24px',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                }}
              >
                <h3
                  style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1f2937',
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Database
                    size={20}
                    style={{ color: 'var(--primary-color)' }}
                  />
                  Natural Language to Database Query
                </h3>
                <button
                  onClick={handleClear}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    backgroundColor: 'transparent',
                    color: '#6b7280',
                    border: '1px solid #d1d5db',
                    borderRadius: '0',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontFamily:
                      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f3f4f6';
                    e.target.style.color = '#1f2937';
                    e.target.style.borderColor = '#9ca3af';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#6b7280';
                    e.target.style.borderColor = '#d1d5db';
                  }}
                >
                  <XCircle size={14} />
                  Clear
                </button>
              </div>

              <div
                style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}
              >
                <div style={{ flex: 1, position: 'relative' }}>
                  <textarea
                    value={nlQuery}
                    onChange={(e) => setNlQuery(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask in natural language: 'Show me all active contracts expiring in the next 30 days' or 'Find assets assigned to John Smith'..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily:
                        'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                      resize: 'vertical',
                    }}
                  />
                </div>
                <button
                  onClick={handleNaturalLanguageQuery}
                  disabled={isConverting || !nlQuery.trim()}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 24px',
                    backgroundColor:
                      isConverting || !nlQuery.trim()
                        ? '#9ca3af'
                        : 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor:
                      isConverting || !nlQuery.trim()
                        ? 'not-allowed'
                        : 'pointer',
                    fontFamily:
                      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    height: 'fit-content',
                  }}
                >
                  {isConverting ? (
                    <>
                      <RefreshCw
                        size={16}
                        style={{ animation: 'spin 1s linear infinite' }}
                      />
                      Converting...
                    </>
                  ) : (
                    <>
                      <Database size={16} />
                      Convert
                    </>
                  )}
                </button>
              </div>

              {/* Query Examples */}
              <div
                style={{
                  backgroundColor: '#f8fafc',
                  borderRadius: '0',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                }}
              >
                <h4
                  style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    margin: '0 0 12px 0',
                  }}
                >
                  Example Queries:
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {[
                    'Show me all active contracts',
                    'Find assets expiring this month',
                    'List contracts with value over $100,000',
                    'Show users in IT department',
                    'Find contracts signed by John Smith',
                  ].map((example, index) => (
                    <button
                      key={index}
                      onClick={() => setNlQuery(example)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#e5e7eb',
                        color: '#374151',
                        border: '1px solid #d1d5db',
                        borderRadius: '0',
                        fontSize: '12px',
                        cursor: 'pointer',
                        fontFamily:
                          'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                      }}
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Query Result Display */}
            {queryResult && (
              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: '0',
                  padding: '24px',
                  marginBottom: '24px',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb',
                }}
              >
                {/* Answer Section */}
                <div
                  style={{
                    backgroundColor: '#f0f9ff',
                    border: '1px solid #0ea5e9',
                    borderRadius: '0',
                    padding: '20px',
                    marginBottom: '24px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                    }}
                  >
                    <Brain
                      size={24}
                      style={{
                        color: '#0ea5e9',
                        flexShrink: 0,
                        marginTop: '2px',
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <h3
                        style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#0c4a6e',
                          margin: '0 0 8px 0',
                        }}
                      >
                        Answer
                      </h3>
                      <p
                        style={{
                          fontSize: '15px',
                          color: '#075985',
                          lineHeight: '1.6',
                          margin: 0,
                        }}
                      >
                        {queryResult.answer ||
                          queryResult.explanation ||
                          'No answer available.'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Charts Section */}
                {queryResult.charts && queryResult.charts.length > 0 && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        queryResult.charts.length === 1
                          ? '1fr'
                          : 'repeat(auto-fit, minmax(400px, 1fr))',
                      gap: '24px',
                      marginBottom: '24px',
                    }}
                  >
                    {queryResult.charts.map((chart, index) => (
                      <div
                        key={index}
                        style={{
                          backgroundColor: '#f8fafc',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0',
                          padding: '20px',
                        }}
                      >
                        <h4
                          style={{
                            fontSize: '15px',
                            fontWeight: '600',
                            color: '#1f2937',
                            margin: '0 0 16px 0',
                          }}
                        >
                          {chart.title}
                        </h4>
                        <div style={{ height: '300px' }}>
                          {chart.type === 'pie' ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={chart.data}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={({ name, percent }) =>
                                    `${name}: ${(percent * 100).toFixed(0)}%`
                                  }
                                  outerRadius={80}
                                  fill="#8884d8"
                                  dataKey="value"
                                >
                                  {chart.data.map((entry, idx) => {
                                    const colors = [
                                      '#0ea5e9',
                                      '#10b981',
                                      '#f59e0b',
                                      '#ef4444',
                                      '#8b5cf6',
                                      '#ec4899',
                                      '#14b8a6',
                                      '#f97316',
                                    ];
                                    return (
                                      <Cell
                                        key={`cell-${idx}`}
                                        fill={colors[idx % colors.length]}
                                      />
                                    );
                                  })}
                                </Pie>
                                <Tooltip />
                              </PieChart>
                            </ResponsiveContainer>
                          ) : chart.type === 'bar' ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={chart.data}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                  dataKey="name"
                                  angle={-45}
                                  textAnchor="end"
                                  height={80}
                                  style={{ fontSize: '12px' }}
                                />
                                <YAxis style={{ fontSize: '12px' }} />
                                <Tooltip />
                                <Bar dataKey="value" fill="#0ea5e9" />
                              </BarChart>
                            </ResponsiveContainer>
                          ) : (
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={chart.data}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                  dataKey="name"
                                  style={{ fontSize: '12px' }}
                                />
                                <YAxis style={{ fontSize: '12px' }} />
                                <Tooltip />
                                <Line
                                  type="monotone"
                                  dataKey="value"
                                  stroke="#0ea5e9"
                                  strokeWidth={2}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Query Results Table */}
                {queryResult.results && queryResult.results.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '16px',
                      }}
                    >
                      <h4
                        style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#1f2937',
                          margin: 0,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <FileText
                          size={18}
                          style={{ color: 'var(--primary-color)' }}
                        />
                        Detailed Results (
                        {queryResult.resultCount || queryResult.results.length}{' '}
                        of{' '}
                        {queryResult.totalCount || queryResult.results.length})
                      </h4>
                      {queryResult.entityType && (
                        <span
                          style={{
                            padding: '4px 12px',
                            backgroundColor: 'var(--primary-color)20',
                            color: 'var(--primary-color)',
                            borderRadius: '0',
                            fontSize: '12px',
                            fontWeight: '500',
                          }}
                        >
                          {queryResult.entityType}
                        </span>
                      )}
                    </div>
                    <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                      <DataTable
                        data={queryResult.results}
                        columns={
                          queryResult.entityType === 'Contract'
                            ? [
                                {
                                  key: 'contractId',
                                  label: 'Contract ID',
                                  width: '120px',
                                },
                                {
                                  key: 'title',
                                  label: 'Title',
                                  width: '200px',
                                },
                                { key: 'type', label: 'Type', width: '100px' },
                                {
                                  key: 'counterparty',
                                  label: 'Counterparty',
                                  width: '150px',
                                },
                                {
                                  key: 'status',
                                  label: 'Status',
                                  width: '100px',
                                },
                                {
                                  key: 'value',
                                  label: 'Value',
                                  width: '120px',
                                  render: (value, row) => {
                                    const currency = row.currency || 'USD';
                                    const amount = value || 0;
                                    return new Intl.NumberFormat('en-US', {
                                      style: 'currency',
                                      currency: currency,
                                    }).format(amount);
                                  },
                                },
                                {
                                  key: 'endDate',
                                  label: 'End Date',
                                  width: '120px',
                                  render: (date) =>
                                    date
                                      ? new Date(date).toLocaleDateString()
                                      : '-',
                                },
                              ]
                            : queryResult.entityType === 'Asset'
                              ? [
                                  {
                                    key: 'assetId',
                                    label: 'Asset ID',
                                    width: '120px',
                                  },
                                  {
                                    key: 'name',
                                    label: 'Name',
                                    width: '200px',
                                  },
                                  {
                                    key: 'type',
                                    label: 'Type',
                                    width: '120px',
                                  },
                                  {
                                    key: 'category',
                                    label: 'Category',
                                    width: '120px',
                                  },
                                  {
                                    key: 'status',
                                    label: 'Status',
                                    width: '100px',
                                  },
                                  {
                                    key: 'value',
                                    label: 'Value',
                                    width: '120px',
                                    render: (value) => {
                                      const amount = value || 0;
                                      return new Intl.NumberFormat('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                      }).format(amount);
                                    },
                                  },
                                  {
                                    key: 'location',
                                    label: 'Location',
                                    width: '150px',
                                  },
                                ]
                              : queryResult.entityType === 'Vendor'
                                ? [
                                    {
                                      key: 'vendorId',
                                      label: 'Vendor ID',
                                      width: '120px',
                                    },
                                    {
                                      key: 'name',
                                      label: 'Name',
                                      width: '200px',
                                    },
                                    {
                                      key: 'type',
                                      label: 'Type',
                                      width: '120px',
                                    },
                                    {
                                      key: 'status',
                                      label: 'Status',
                                      width: '100px',
                                    },
                                    {
                                      key: 'email',
                                      label: 'Email',
                                      width: '180px',
                                    },
                                    {
                                      key: 'phone',
                                      label: 'Phone',
                                      width: '120px',
                                    },
                                  ]
                                : queryResult.entityType === 'Customer'
                                  ? [
                                      {
                                        key: 'customerId',
                                        label: 'Customer ID',
                                        width: '120px',
                                      },
                                      {
                                        key: 'name',
                                        label: 'Name',
                                        width: '200px',
                                      },
                                      {
                                        key: 'type',
                                        label: 'Type',
                                        width: '120px',
                                      },
                                      {
                                        key: 'status',
                                        label: 'Status',
                                        width: '100px',
                                      },
                                      {
                                        key: 'email',
                                        label: 'Email',
                                        width: '180px',
                                      },
                                      {
                                        key: 'phone',
                                        label: 'Phone',
                                        width: '120px',
                                      },
                                    ]
                                  : queryResult.entityType === 'Project'
                                    ? [
                                        {
                                          key: 'projectId',
                                          label: 'Project ID',
                                          width: '120px',
                                        },
                                        {
                                          key: 'name',
                                          label: 'Name',
                                          width: '200px',
                                        },
                                        {
                                          key: 'status',
                                          label: 'Status',
                                          width: '100px',
                                        },
                                        {
                                          key: 'budget',
                                          label: 'Budget',
                                          width: '120px',
                                          render: (value) => {
                                            const amount = value || 0;
                                            return new Intl.NumberFormat(
                                              'en-US',
                                              {
                                                style: 'currency',
                                                currency: 'USD',
                                              },
                                            ).format(amount);
                                          },
                                        },
                                        {
                                          key: 'startDate',
                                          label: 'Start Date',
                                          width: '120px',
                                          render: (date) =>
                                            date
                                              ? new Date(
                                                  date,
                                                ).toLocaleDateString()
                                              : '-',
                                        },
                                        {
                                          key: 'endDate',
                                          label: 'End Date',
                                          width: '120px',
                                          render: (date) =>
                                            date
                                              ? new Date(
                                                  date,
                                                ).toLocaleDateString()
                                              : '-',
                                        },
                                      ]
                                    : [
                                        {
                                          key: 'entityType',
                                          label: 'Type',
                                          width: '100px',
                                        },
                                        {
                                          key: 'name',
                                          label: 'Name',
                                          width: '200px',
                                        },
                                        {
                                          key: 'title',
                                          label: 'Title',
                                          width: '200px',
                                        },
                                        {
                                          key: 'status',
                                          label: 'Status',
                                          width: '100px',
                                        },
                                      ]
                        }
                        title=""
                        searchable={true}
                        sortable={true}
                        exportable={true}
                        pagination={true}
                        pageSize={10}
                        hiddenColumns={['none']}
                        onHiddenColumnsChange={() => {}}
                      />
                    </div>
                  </div>
                )}

                {queryResult.results &&
                  queryResult.results.length === 0 &&
                  !queryResult.answer && (
                    <div
                      style={{
                        padding: '40px',
                        textAlign: 'center',
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0',
                        marginBottom: '20px',
                      }}
                    >
                      <AlertCircle
                        size={48}
                        style={{ color: '#9ca3af', marginBottom: '16px' }}
                      />
                      <p
                        style={{
                          margin: 0,
                          color: '#6b7280',
                          fontSize: '15px',
                          fontWeight: '500',
                        }}
                      >
                        No results found matching your query.
                      </p>
                      <p
                        style={{
                          margin: '8px 0 0 0',
                          color: '#9ca3af',
                          fontSize: '14px',
                        }}
                      >
                        Try adjusting your search criteria or using different
                        keywords.
                      </p>
                    </div>
                  )}
              </div>
            )}

            {/* Query History */}
            {queryHistory.length > 0 && (
              <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                <DataTable
                  data={queryHistory}
                  columns={[
                    {
                      key: 'naturalLanguage',
                      label: 'Natural Language Query',
                      width: '300px',
                    },
                    {
                      key: 'queryResult',
                      label: 'Query Type',
                      width: '120px',
                      render: (result) => result.queryType,
                    },
                    {
                      key: 'confidence',
                      label: 'Confidence',
                      width: '100px',
                      render: (confidence) => `${confidence}%`,
                    },
                    {
                      key: 'timestamp',
                      label: 'Converted',
                      width: '120px',
                      render: (date) => new Date(date).toLocaleDateString(),
                    },
                  ]}
                  title={`Query History (${queryHistory.length} queries)`}
                  searchable={false}
                  sortable={true}
                  exportable={true}
                  pagination={true}
                  pageSize={10}
                  hiddenColumns={['none']}
                  onHiddenColumnsChange={() => {}}
                />
              </div>
            )}
          </div>
        )}

        {/* Search History Tab */}
        {activeTab === 'history' && (
          <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
            <DataTable
              data={chatData}
              columns={[
                {
                  key: 'question',
                  label: 'Question',
                  width: '300px',
                },
                {
                  key: 'category',
                  label: 'Category',
                  width: '120px',
                },
                {
                  key: 'confidence',
                  label: 'Confidence',
                  width: '100px',
                  render: (confidence) => `${confidence}%`,
                },
                {
                  key: 'timestamp',
                  label: 'Asked',
                  width: '120px',
                  render: (date) => new Date(date).toLocaleDateString(),
                },
              ]}
              title={`Q&A History (${chatData.length} questions)`}
              searchable={false}
              sortable={true}
              exportable={true}
              pagination={true}
              pageSize={10}
              hiddenColumns={['none']}
              onHiddenColumnsChange={() => {}}
            />
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div>
            {/* Analytics Header */}
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '0',
                padding: '24px',
                marginBottom: '24px',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <h3
                  style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#1f2937',
                    margin: '0 0 8px 0',
                  }}
                >
                  AI Search & Q&A Analytics
                </h3>
                <p
                  style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    margin: 0,
                  }}
                >
                  Comprehensive insights into search performance and user
                  behavior
                </p>
              </div>
              <div
                style={{ display: 'flex', gap: '12px', alignItems: 'center' }}
              >
                <select
                  value={selectedTimeframe}
                  onChange={(e) => {
                    setSelectedTimeframe(e.target.value);
                    fetchAnalytics(e.target.value);
                  }}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily:
                      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  }}
                >
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="90d">Last 90 days</option>
                  <option value="1y">Last year</option>
                </select>
                <button
                  onClick={() => fetchAnalytics(selectedTimeframe)}
                  disabled={analyticsLoading}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    backgroundColor: analyticsLoading
                      ? '#9ca3af'
                      : 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: analyticsLoading ? 'not-allowed' : 'pointer',
                    fontFamily:
                      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  }}
                >
                  {analyticsLoading ? (
                    <>
                      <RefreshCw
                        size={14}
                        style={{ animation: 'spin 1s linear infinite' }}
                      />
                      Loading...
                    </>
                  ) : (
                    <>
                      <RefreshCw size={14} />
                      Refresh
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Analytics Content */}
            {analyticsLoading ? (
              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: '0',
                  padding: '40px',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '400px',
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <RefreshCw
                    size={32}
                    style={{
                      animation: 'spin 1s linear infinite',
                      color: 'var(--primary-color)',
                      marginBottom: '16px',
                    }}
                  />
                  <p style={{ fontSize: '16px', color: '#6b7280', margin: 0 }}>
                    Loading analytics data...
                  </p>
                </div>
              </div>
            ) : analyticsData ? (
              <div style={{ display: 'grid', gap: '24px' }}>
                {/* Key Metrics */}
                <div
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '0',
                    padding: '24px',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <h4
                    style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#1f2937',
                      margin: '0 0 20px 0',
                    }}
                  >
                    Key Performance Metrics
                  </h4>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '20px',
                    }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: '32px',
                          fontWeight: '700',
                          color: 'var(--primary-color)',
                          marginBottom: '8px',
                        }}
                      >
                        {analyticsData.metrics.totalSearches}
                      </div>
                      <div style={{ fontSize: '14px', color: '#6b7280' }}>
                        Total Searches
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: '32px',
                          fontWeight: '700',
                          color: '#10b981',
                          marginBottom: '8px',
                        }}
                      >
                        {analyticsData.metrics.totalQuestions}
                      </div>
                      <div style={{ fontSize: '14px', color: '#6b7280' }}>
                        Questions Asked
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: '32px',
                          fontWeight: '700',
                          color: '#f59e0b',
                          marginBottom: '8px',
                        }}
                      >
                        {analyticsData.metrics.avgRelevanceScore}%
                      </div>
                      <div style={{ fontSize: '14px', color: '#6b7280' }}>
                        Avg. Relevance
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: '32px',
                          fontWeight: '700',
                          color: '#8b5cf6',
                          marginBottom: '8px',
                        }}
                      >
                        {analyticsData.metrics.userSatisfaction}%
                      </div>
                      <div style={{ fontSize: '14px', color: '#6b7280' }}>
                        User Satisfaction
                      </div>
                    </div>
                  </div>
                </div>

                {/* Search Trends */}
                <div
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '0',
                    padding: '24px',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <h4
                    style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#1f2937',
                      margin: '0 0 20px 0',
                    }}
                  >
                    Popular Search Queries
                  </h4>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                    }}
                  >
                    {analyticsData.searchTrends.popularQueries.map(
                      (query, index) => (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px 16px',
                            backgroundColor: '#f8fafc',
                            borderRadius: '0',
                            border: '1px solid #e5e7eb',
                          }}
                        >
                          <span
                            style={{
                              fontSize: '14px',
                              color: '#374151',
                              fontWeight: '500',
                            }}
                          >
                            {query.query}
                          </span>
                          <span
                            style={{
                              fontSize: '14px',
                              color: 'var(--primary-color)',
                              fontWeight: '600',
                              backgroundColor: 'var(--primary-color)10',
                              padding: '4px 8px',
                              borderRadius: '0',
                            }}
                          >
                            {query.count} searches
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                </div>

                {/* Performance Metrics */}
                <div
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '0',
                    padding: '24px',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <h4
                    style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#1f2937',
                      margin: '0 0 20px 0',
                    }}
                  >
                    System Performance
                  </h4>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: '20px',
                    }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: '24px',
                          fontWeight: '600',
                          color: '#10b981',
                          marginBottom: '4px',
                        }}
                      >
                        {analyticsData.metrics.avgResponseTime}s
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        Avg Response Time
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: '24px',
                          fontWeight: '600',
                          color: '#10b981',
                          marginBottom: '4px',
                        }}
                      >
                        {analyticsData.performance?.systemUptime || 99.8}%
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        System Uptime
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: '24px',
                          fontWeight: '600',
                          color: '#ef4444',
                          marginBottom: '4px',
                        }}
                      >
                        {analyticsData.performance?.errorRate || 0.2}%
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        Error Rate
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: '0',
                  padding: '40px',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '400px',
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <BarChart3
                    size={48}
                    style={{ color: '#9ca3af', marginBottom: '16px' }}
                  />
                  <p style={{ fontSize: '16px', color: '#6b7280', margin: 0 }}>
                    No analytics data available
                  </p>
                  <button
                    onClick={() => fetchAnalytics(selectedTimeframe)}
                    style={{
                      marginTop: '16px',
                      padding: '8px 16px',
                      backgroundColor: 'var(--primary-color)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0',
                      fontSize: '14px',
                      cursor: 'pointer',
                    }}
                  >
                    Load Analytics
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Document"
          message={`Are you sure you want to delete "${selectedItem?.title}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          isLoading={isDeleting}
        />

        <style jsx="true">{`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          @keyframes pulse {
            0%,
            100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}</style>
      </div>
      <ToastContainer />
    </>
  );
};

export default AISearchQA;
